// Modal principal para gestionar productos externos excluidos
// Permite ver el listado de exclusiones, crear nuevas y eliminar existentes
import { forwardRef, useImperativeHandle, useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Product } from '@shared/components/ui/Product';
import { Shop } from '@shared/components/ui/shop';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faShop } from '@fortawesome/pro-light-svg-icons';
import { useExternalExcludedProducts, useDeleteExclusion } from '../../hooks/useExternalExcludedProducts';
import { LoadingState } from '../feedback';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { useToast } from '@shared/hooks/use-toast';
import { CreateExclusionModal, CreateExclusionModalRef } from './CreateExclusionModal';
import { ShopApiAdapter } from '@modules/Products/CreateEdit/adapters/ShopApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

export interface ExternalExcludedProductsModalRef {
  openModal: () => void;
  closeModal: () => void;
}

export const ExternalExcludedProductsModal = forwardRef<ExternalExcludedProductsModalRef>((_, ref) => {
  const { t } = useTranslation('productsModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const createModalRef = useRef<CreateExclusionModalRef>(null);
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const deleteMutation = useDeleteExclusion();

  // Estado para el filtro de tienda
  const [selectedShopId, setSelectedShopId] = useState<string>('');
  const [shops, setShops] = useState<{ label: string; value: string }[]>([]);
  const [shopsData, setShopsData] = useState<
    Array<{ id: number; name: string; image_hash?: string | null; image?: string | null }>
  >([]);

  // Convertir shopId a number para la query
  const shopIdForQuery = useMemo(() => {
    const shopId = selectedShopId && selectedShopId !== '' ? Number(selectedShopId) : undefined;
    console.log('🔍 ExternalExcludedProductsModal - shopIdForQuery:', {
      selectedShopId,
      shopIdForQuery: shopId,
    });
    return shopId;
  }, [selectedShopId]);

  // Obtener exclusiones
  const { data: excludedProductsRaw = [], isLoading, error, refetch } = useExternalExcludedProducts(shopIdForQuery);

  // Filtrar productos por tienda en el frontend como medida de seguridad
  // Esto asegura que solo se muestren productos de la tienda seleccionada
  const excludedProducts = useMemo(() => {
    if (!shopIdForQuery) {
      // Si no hay filtro de tienda, mostrar todos
      return excludedProductsRaw;
    }
    // Filtrar por shop_id
    const filtered = excludedProductsRaw.filter((product) => product.shop_id === shopIdForQuery);
    console.log('🔍 ExternalExcludedProductsModal - Filtered products:', {
      shopIdForQuery,
      total: excludedProductsRaw.length,
      filtered: filtered.length,
      products: filtered.map((p) => ({ id: p.id, shop_id: p.shop_id, shop_name: p.shop_name })),
    });
    return filtered;
  }, [excludedProductsRaw, shopIdForQuery]);

  // Log cuando cambian los productos excluidos
  useEffect(() => {
    console.log('🔍 ExternalExcludedProductsModal - excludedProducts (raw):', {
      shopIdForQuery,
      count: excludedProductsRaw.length,
      products: excludedProductsRaw.map((p) => ({ id: p.id, shop_id: p.shop_id, shop_name: p.shop_name })),
    });
  }, [excludedProductsRaw, shopIdForQuery]);

  // Cargar tiendas al montar
  useEffect(() => {
    const httpClient = createHttpClient(apiClientWH);
    const adapter = new ShopApiAdapter(httpClient);
    adapter
      .getAll()
      .then((data) => {
        // Guardar datos completos de tiendas para usar con componente Shop
        setShopsData(data);
        // Guardar formato para el Combobox
        setShops(
          data.map((shop) => ({
            value: String(shop.id),
            label: shop.name,
          })),
        );
      })
      .catch(() => {
        // Error al cargar tiendas - se puede manejar si es necesario
      });
  }, []);

  // Función helper para encontrar la tienda por ID
  const getShopById = useCallback(
    (shopId: number) => {
      return shopsData.find((shop) => shop.id === shopId);
    },
    [shopsData],
  );

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setSelectedShopId('');
      modalRef.current?.openModal();
    },
    closeModal: () => {
      modalRef.current?.closeModal();
    },
  }));

  const handleCreateClick = () => {
    createModalRef.current?.openModal();
  };

  const handleDeleteClick = (id: number, productTitle?: string) => {
    confirmModalRef.current?.open(
      async () => {
        try {
          console.log('🔍 ExternalExcludedProductsModal - Deleting exclusion:', {
            id,
          });

          const response = await deleteMutation.mutateAsync(id);

          console.log('🔍 ExternalExcludedProductsModal - Delete response:', response);

          if (response.success) {
            toast({
              title: t('external_excluded.deleteSuccess.title'),
              description: response.message || t('external_excluded.deleteSuccess.description'),
              variant: 'default',
            });
            refetch();
          } else {
            toast({
              title: t('external_excluded.deleteError.title'),
              description: response.message,
              variant: 'destructive',
            });
          }
        } catch (error: any) {
          console.log('🔍 ExternalExcludedProductsModal - Delete error:', error);
          toast({
            title: t('external_excluded.deleteError.title'),
            description:
              error?.response?.data?.message || error?.message || t('external_excluded.deleteError.unexpected'),
            variant: 'destructive',
          });
        }
      },
      undefined,
      t('external_excluded.deleteConfirm.title'),
      t('external_excluded.deleteConfirm.message', {
        product: productTitle || t('external_excluded.product'),
      }),
    );
  };

  const handleCreateSuccess = () => {
    refetch();
  };

  const getAmountHandlingLabel = (handling: string) => {
    return handling === 'remove'
      ? t('external_excluded.amountHandling.remove')
      : t('external_excluded.amountHandling.addToShipping');
  };

  return (
    <>
      <ModalCenter
        ref={modalRef}
        title={t('external_excluded.modal.title')}
        type="default"
        width="lg"
        className="h-[100dvh] sm:h-auto sm:max-h-[90vh] w-full sm:w-[800px]"
      >
        <div className="flex flex-col h-full sm:h-auto">
          <div className="space-y-4 pt-3 sm:pb-2 flex-1 overflow-hidden flex flex-col">
            {/* Header Section - Sección de encabezado con filtro */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Shop Filter - Filtro de tienda */}
              <div className="flex-1 min-w-[200px]">
                <Combobox
                  options={[{ label: t('external_excluded.allShops'), value: '' }, ...shops]}
                  value={selectedShopId}
                  setValue={setSelectedShopId}
                  placeholder={t('external_excluded.filterByShop')}
                  dataTestId="shop-filter-combobox"
                />
              </div>
            </div>

            {/* Content Section - Sección de contenido */}
            <div className="pt-2 overflow-y-auto flex-1 max-h-[calc(90vh-155px)] sm:max-h-[calc(90vh-250px)]">
              {isLoading && <LoadingState message={t('external_excluded.loading')} className="h-32" />}

              {error && (
                <div className="text-center py-8">
                  <p className="text-error mb-4">{t('external_excluded.error.loading')}</p>
                  <Button variant="outline" size="lg" onClick={() => refetch()} data-testid="retry-button">
                    {t('external_excluded.retry')}
                  </Button>
                </div>
              )}

              {!isLoading && !error && excludedProducts.length === 0 && (
                <EmptyView
                  type="emptyState"
                  icon={EMPTY_VIEW_ICONS.PRODUCTS}
                  title={t('external_excluded.empty.title') /*t('external_excluded.empty.description')*/}
                  description=" "
                  className="!pb-1 !pt-3 min-h-fit"
                />
              )}

              {!isLoading && !error && excludedProducts.length > 0 && (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {excludedProducts.map((product) => (
                    <div key={product.id} className="border border-card-border rounded-lg p-4">
                      {/* Product Info Row - Fila de información del producto */}
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <div className="flex-1">
                          {/* Product Component - Componente de producto */}
                          <Product
                            id={product.id}
                            name={product.external_shop_product_title || t('external_excluded.product')}
                            src=""
                            code={`external Id: ${product.external_product_id}`}
                            showImageModal={false}
                            enableLineClamp={false}
                            data-testid={`excluded-product-${product.id}`}
                          />

                          {/* Product Details - Detalles del producto */}
                          <div className="flex items-center gap-4 text-sm text-subtext mt-2 w-full">
                            {/* Amount Handling - Manejo de importe (solo resultado) */}
                            <div className="text-sm font-medium text-complementary-text">
                              {getAmountHandlingLabel(product.excluded_products_amount_handling)}
                            </div>

                            {/* Shop - Tienda */}
                            <div className="flex items-center pl-1">
                              {' '}
                              {(() => {
                                const shop = getShopById(product.shop_id);
                                return shop ? (
                                  <Shop
                                    name={shop.name}
                                    src={shop.image_hash || shop.image || undefined}
                                    className="py-0"
                                  />
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faShop} className="w-3.5 h-3.5 text-icons" />
                                    <span>{product.shop_name || `ID: ${product.shop_id}`}</span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Delete Button - Botón de eliminar */}
                        <Button
                          variant="outline"
                          size="lg"
                          onClick={() => handleDeleteClick(product.id, product.external_shop_product_title)}
                          disabled={deleteMutation.isPending}
                          data-testid={`delete-exclusion-${product.id}-button`}
                        >
                          <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer Section - Sección de pie con botones */}
          <div className="flex justify-start sm:justify-end gap-2 pt-2 sm:pt-4 pb-2 sm:pb-0 sm:border-t-0 mt-auto sm:mt-0">
            <Button
              variant="outline"
              size="lg"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="close-exclusion-modal-button"
            >
              {t('external_excluded.close')}
            </Button>
            <Button variant="primary" size="lg" onClick={handleCreateClick} data-testid="create-exclusion-button">
              {t('external_excluded.createExclusion')}
            </Button>
          </div>
        </div>
      </ModalCenter>

      <CreateExclusionModal ref={createModalRef} onSuccess={handleCreateSuccess} />
      <ConfirmModal ref={confirmModalRef} />
    </>
  );
});

ExternalExcludedProductsModal.displayName = 'ExternalExcludedProductsModal';
